import React, { useEffect, useState } from 'react';
import { ArrowRight, Flame, Users } from 'lucide-react';
import { publicApi } from '@/lib/api';

export default function LaunchPromoBanner({ compact = false, className = '', onCta }) {
  const [promo, setPromo] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const response = await publicApi.offers();
        if (!cancelled) setPromo(response?.data?.launch_promo || null);
      } catch {
        if (!cancelled) setPromo(null);
      }
    };
    load();
    const timer = window.setInterval(load, 15000);
    return () => { cancelled = true; window.clearInterval(timer); };
  }, []);

  if (!promo) return null;

  const remaining = Math.max(0, Number(promo.remaining || 0));
  const used = Math.max(0, Number(promo.used || 0));
  const limit = Math.max(1, Number(promo.limit || 1000));
  const active = Boolean(promo.active) && remaining > 0;
  const progress = Math.min(100, Math.max(0, (used / limit) * 100));

  if (!active) {
    return (
      <div className={`rounded-2xl border border-slate-200 bg-white p-4 shadow-sm ${className}`}>
        <div className="flex items-center gap-3">
          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-blue-50 text-blue-600"><Users className="h-5 w-5" /></div>
          <div>
            <div className="text-[11px] font-black uppercase tracking-[0.16em] text-slate-400">Promotion terminée</div>
            <div className="mt-0.5 text-lg font-black text-slate-950">LoyerBook · 3 USD / mois</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`relative overflow-hidden rounded-2xl border border-amber-300 bg-gradient-to-br from-amber-50 via-orange-50 to-white shadow-lg shadow-amber-900/10 ${compact ? 'p-4 sm:p-5' : 'p-5 sm:p-7'} ${className}`}>
      <div className="absolute -right-10 -top-10 h-28 w-28 rounded-full bg-amber-300/25 blur-2xl" />
      <div className="relative">
        <div className="flex items-center gap-2 text-[11px] font-black uppercase tracking-[0.18em] text-orange-700">
          <span className="relative flex h-2.5 w-2.5"><span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-orange-500 opacity-50" /><span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-orange-500" /></span>
          Promotion de lancement
        </div>

        <div className="mt-3 grid gap-4 sm:grid-cols-[1fr_auto] sm:items-end">
          <div>
            <div className="flex items-baseline gap-2">
              <span className={`${compact ? 'text-4xl' : 'text-5xl'} font-black tracking-tight text-slate-950`}>{remaining.toLocaleString('fr-FR')}</span>
              <span className="text-sm font-black uppercase text-orange-700">places restantes</span>
            </div>
            <div className="mt-1 text-sm font-bold text-slate-700">sur les {limit.toLocaleString('fr-FR')} premiers bailleurs</div>
          </div>
          <div className="rounded-xl bg-slate-950 px-4 py-3 text-white sm:text-right">
            <div className="text-2xl font-black">1 USD</div>
            <div className="text-[11px] font-bold text-white/65">1er mois · puis 3 USD/mois</div>
          </div>
        </div>

        <div className="mt-4">
          <div className="mb-2 flex items-center justify-between text-[11px] font-bold text-slate-500">
            <span>{used.toLocaleString('fr-FR')} place{used > 1 ? 's' : ''} déjà prise{used > 1 ? 's' : ''}</span>
            <span>{Math.round(progress)}%</span>
          </div>
          <div className="h-3 overflow-hidden rounded-full bg-white ring-1 ring-amber-200">
            <div className="h-full rounded-full bg-gradient-to-r from-orange-500 to-amber-400 transition-all duration-700" style={{ width: `${progress}%` }} />
          </div>
        </div>

        <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-2 text-xs font-bold text-orange-800"><Flame className="h-4 w-4" /> Chaque nouvelle inscription fait baisser ce compteur.</div>
          {onCta && <button type="button" onClick={onCta} className="inline-flex h-10 items-center justify-center gap-2 rounded-xl bg-orange-600 px-4 text-xs font-black text-white hover:bg-orange-700">Profiter de l’offre <ArrowRight className="h-4 w-4" /></button>}
        </div>
      </div>
    </div>
  );
}
